/**
 * Pure TEA update for thesaurus look-up panel state.
 *
 * Shell: `useWordLookupStore.ts` (Pinia + lookup Cmd + editor replace port).
 * `getView` stays in the shell (editor island registration).
 */
import { assertNever } from "../lib/assertNever";
import { isJust, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";
import type { WordSense } from "../lib/wordLookup";

export type WordReplaceRange = {
  readonly from: number;
  readonly to: number;
  /** Text that was at `[from, to]` when look-up was seeded. */
  readonly expected: string;
};

export type WordLookupSeed = {
  readonly text: string;
  readonly from: number;
  readonly to: number;
};

export type WordLookupModel = {
  readonly query: string;
  readonly senses: readonly WordSense[];
  readonly resolvedLemma: Maybe<string>;
  readonly loading: boolean;
  readonly error: Maybe<string>;
  readonly lookedUp: boolean;
  readonly replaceRange: Maybe<WordReplaceRange>;
  readonly focusSearchToken: number;
  readonly lookupGeneration: number;
};

export const initialWordLookupModel: WordLookupModel = {
  query: "",
  senses: [],
  resolvedLemma: nothing,
  loading: false,
  error: nothing,
  lookedUp: false,
  replaceRange: nothing,
  focusSearchToken: 0,
  lookupGeneration: 0,
};

export type WordLookupMsg =
  | { readonly type: "ClearReplaceRange" }
  | { readonly type: "SetReplaceRange"; readonly range: WordReplaceRange }
  | { readonly type: "RequestSearchFocus" }
  | { readonly type: "OpenFromEditor"; readonly seed: Maybe<WordLookupSeed> }
  | { readonly type: "SubmitSearch"; readonly raw: string }
  | { readonly type: "LookupEmpty" }
  | {
      readonly type: "LookupStarted";
      readonly query: string;
      readonly generation: number;
    }
  | {
      readonly type: "LookupSucceeded";
      readonly generation: number;
      readonly senses: readonly WordSense[];
      readonly resolvedLemma: Maybe<string>;
    }
  | {
      readonly type: "LookupFailed";
      readonly generation: number;
    };

export type WordLookupCmd = {
  readonly kind: "lookup";
  readonly query: string;
  readonly generation: number;
};

export function updateWordLookup(
  model: WordLookupModel,
  msg: WordLookupMsg,
): readonly [WordLookupModel, readonly WordLookupCmd[]] {
  switch (msg.type) {
    case "ClearReplaceRange":
      return [{ ...model, replaceRange: nothing }, []];
    case "SetReplaceRange":
      return [{ ...model, replaceRange: just(msg.range) }, []];
    case "RequestSearchFocus":
      return [{ ...model, focusSearchToken: model.focusSearchToken + 1 }, []];
    case "OpenFromEditor": {
      if (isJust(msg.seed) === false || msg.seed.value.text.length === 0) {
        return [
          {
            ...model,
            replaceRange: nothing,
            focusSearchToken: model.focusSearchToken + 1,
          },
          [],
        ];
      }
      const seed = msg.seed.value;
      const generation = model.lookupGeneration + 1;
      return [
        {
          ...model,
          query: seed.text,
          replaceRange: just({
            from: seed.from,
            to: seed.to,
            expected: seed.text,
          }),
          loading: true,
          error: nothing,
          lookedUp: true,
          lookupGeneration: generation,
        },
        [{ kind: "lookup", query: seed.text.trim(), generation }],
      ];
    }
    case "SubmitSearch": {
      const q = msg.raw.trim();
      if (q.length === 0) {
        return [
          {
            ...model,
            query: q,
            replaceRange: nothing,
            senses: [],
            resolvedLemma: nothing,
            lookedUp: false,
            error: nothing,
            loading: false,
          },
          [],
        ];
      }
      const generation = model.lookupGeneration + 1;
      return [
        {
          ...model,
          query: q,
          replaceRange: nothing,
          loading: true,
          error: nothing,
          lookedUp: true,
          lookupGeneration: generation,
        },
        [{ kind: "lookup", query: q, generation }],
      ];
    }
    case "LookupEmpty":
      return [
        {
          ...model,
          senses: [],
          resolvedLemma: nothing,
          lookedUp: false,
          error: nothing,
          loading: false,
        },
        [],
      ];
    case "LookupStarted":
      return [
        {
          ...model,
          query: msg.query,
          loading: true,
          error: nothing,
          lookedUp: true,
          lookupGeneration: msg.generation,
        },
        [
          {
            kind: "lookup",
            query: msg.query.trim(),
            generation: msg.generation,
          },
        ],
      ];
    case "LookupSucceeded":
      if (msg.generation !== model.lookupGeneration) return [model, []];
      return [
        {
          ...model,
          senses: msg.senses,
          resolvedLemma: msg.resolvedLemma,
          loading: false,
        },
        [],
      ];
    case "LookupFailed":
      if (msg.generation !== model.lookupGeneration) return [model, []];
      return [
        {
          ...model,
          senses: [],
          resolvedLemma: nothing,
          error: just("Look-up failed. Try again."),
          loading: false,
        },
        [],
      ];
    default:
      return assertNever(msg);
  }
}

export const updateWordLookupFn: UpdateFn<
  WordLookupModel,
  WordLookupMsg,
  WordLookupCmd
> = updateWordLookup;
